import { useNavigate } from 'react-router-dom';

import useMount from 'hooks/useMount';
import { IPlayerNotification, PlayerNotificationType } from 'types/notifications';
import useNotifications from 'store/notifications';

import sendMessage from '../common/sendMessage';
import { IMessageHandler } from '../common/types';
import addMessageHandler from '../common/listener';

import { INativeNotificationsInfo, ReceiveNativeNotificationsMessages, SendNativeNotificationsMessages } from './types';
import { NotificationService } from 'api/notifications';

const useNativeNotifications = (): INativeNotificationsInfo => {
  const enableNotifications = (): void => {
    sendMessage({ type: SendNativeNotificationsMessages.enableNotifications });
  };
  const navigate = useNavigate();
  const [, notificationActions] = useNotifications();

  useMount(() => {
    const listener: IMessageHandler<ReceiveNativeNotificationsMessages> = async ({ type, payload }) => {
      switch (type) {
        case ReceiveNativeNotificationsMessages.enableNotifications: {
          NotificationService.updateNativeNotificationsToken(payload.token as string);
          break;
        }
        case ReceiveNativeNotificationsMessages.nativeNotification: {
          const notification = payload as {
            /**
             * Notification title - the bold text displayed above the rest of the content.
             */
            title: string | null;
            /**
             * On Android: `subText` - the display depends on the device.
             *
             * On iOS: `subtitle` - the bold text displayed between title and the rest of the content.
             */
            subtitle: string | null;
            /**
             * Notification body - the main content of the notification.
             */
            body: string | null;
            /**
             * Data associated with the notification, not displayed
             */
            data: Record<string, unknown>;
            // @docsMissing
            sound: 'default' | 'defaultCritical' | 'custom' | null;
          };

          const backendLikeNotification = {
            seenAt: null,
            createdAt: Date.now(),
            name: notification.title || '',
            content: notification.body || '',
            subject: notification.subtitle || '',
            id: Number(notification.data.id) || 0,
            meta: notification.data,
            playerId: notification.data.playerId,
            template: {
              type: notification.data.actionId as PlayerNotificationType,
              translations: [
                {
                  title: notification.title || '',
                  languageId: notification.data.languageId || 1,
                  description: notification.body || '',
                },
              ],
            },
          } as unknown as IPlayerNotification;
          notificationActions.addNotification(backendLikeNotification);
          setTimeout(() => {
            switch (backendLikeNotification.template.type) {
              case PlayerNotificationType.ACCOUNT_VERIFIED: {
                break;
              }
              case PlayerNotificationType.AVAILABLE_BONUS: {
                navigate('/bonuses');
                break;
              }
              case PlayerNotificationType.AVAILABLE_BONUS_EXPIRED: {
                navigate('/bonus-history');
                break;
              }
              case PlayerNotificationType.WITHDRAW_APPROVED: {
                navigate('/wallet-history');
                break;
              }
              case PlayerNotificationType.DEPOSIT_SETTLED: {
                navigate('/wallet-history');
                break;
              }
              case PlayerNotificationType.WITHDRAW_REJECTED: {
                navigate('/wallet-history');
                break;
              }
              default:
                break;
            }
          }, 200);

          notificationActions.updateNotificationById(backendLikeNotification.id);
          break;
        }
        default:
          break;
      }
    };

    return addMessageHandler(listener);
  });

  return { enableNotifications };
};

export default useNativeNotifications;
